import menuItem = require("../model/menu.item");
import reportsCommon = require("../reports/reportscommon");
import { Injectable } from "@angular/core";
import { CrewPermissionsService, CrewPermissions } from "./crew-permissions.service";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { combineLatest } from "rxjs/observable/combineLatest";
import { downgradeInjectable } from "@angular/upgrade/static";
import { ShowTimeBalanceMailTeaserService } from "./show-time-balance-mail-teaser.service";

export function initialize(module: ng.IModule) {
    module.service(MenuDataService.Id, downgradeInjectable(MenuDataService));
}

@Injectable()
export class MenuDataService {

    public static Id = "MenuDataService";

    constructor(
        private crewPermissionsService: CrewPermissionsService,
        private showTimeBalanceMailTeaserService: ShowTimeBalanceMailTeaserService) { }

    public getMenuItems(): Observable<menuItem.MenuItem[]> {
        return combineLatest(this.crewPermissionsService.getPermissions(), this.showTimeBalanceMailTeaserService.showTimeBalanceMailTeaser()).pipe(
            map(([permissions, showTimeBalanceMailTeaser]) => this.createMenuItems(permissions, showTimeBalanceMailTeaser)));
    }

    private createMenuItems(permissions: CrewPermissions, showTimeBalanceMailTeaser: boolean): menuItem.MenuItem[] {
        return this.getMenuItemBuilder()
            .withChildMenuItemBuilder(this.createResourcesMenuItem())
            .withChildMenuItemBuilder(this.createTimeManagementMenuItem(permissions, showTimeBalanceMailTeaser))
            .withChildMenuItemBuilder(this.createComplianceMenuItem())
            .withChildMenuItemBuilder(this.createSearchAndSummationMenuItem())
            .withChildMenuItemBuilder(this.createSetupMenuItem())
            .withChildMenuItemBuilder(this.getMenuBuilder("Users", "person", "app.settings.applicationusers").showIf(permissions.isPermissionAdmin))
            .buildItems();
    }

    private createResourcesMenuItem(): MenuBuilder {
        return this.getMenuBuilder("Resources", "group")
            .withChildMenuItem("Persons", "group", "app.resources.persons")
            .withChildMenuItem("Contact Info", "download.svg", "app." + reportsCommon.ReportsStateStrings.PersonContactInfo)
            .withChildMenuItem("Next of Kin", "download.svg", "app." + reportsCommon.ReportsStateStrings.PersonNextOfKin)
            .withChildMenuItem("Competences", "download.svg", "app." + reportsCommon.ReportsStateStrings.PersonCompetences)
            .withChildMenuItem("Competence List", "download.svg", "app." + reportsCommon.ReportsStateStrings.PersonCompetenceList)
            .withChildMenuItem("CV", "download.svg", "app." + reportsCommon.ReportsStateStrings.PersonCV);
    }

    private createTimeManagementMenuItem(permissions: CrewPermissions, showTimeBalanceMailTeaser: boolean): MenuBuilder {
        return this.getMenuBuilder("Time Management", "event")
            .withChildMenuItemBuilder(
                this.getMenuBuilder("Daily Activities", "event", "app.activities.day", "app.activities.day.with-params").showIf(permissions.isPermissionWriteOrAdmin))
            .withChildMenuItem("Overview", "event", "app.activities.overview", "app.activities.overview.with-params")
            .withChildMenuItemBuilder(
                this.getMenuBuilder("Scheduling", "event", "app.activities.workschedulemusters").showIf(permissions.isPermissionWriteOrAdmin))
            .withChildMenuItemBuilder(
                this.getMenuBuilder("Clear Activities", "event_busy", "app.activities.clearactivities", "app.activities.clearactivities.with-params").showIf(permissions.isPermissionWriteOrAdmin))
            .withChildMenuItem("Adjustments", "plus-minus.svg", "app.activities.activityadjustments", "app.activities.activityadjustments.with-params")
            .withChildMenuItem("Person Balance", "download.svg", "app." + reportsCommon.ReportsStateStrings.PersonTimeBalance)
            .withChildMenuItemBuilder(
                this.getMenuBuilder("Time Balance Mail", "mail_outline", "app.activities.timebalancemail").showIf(showTimeBalanceMailTeaser))
            .withChildMenuItem("Workplace Balance", "download.svg", "app." + reportsCommon.ReportsStateStrings.WorkplaceScheduleTimetable);
    }

    private createComplianceMenuItem(): MenuBuilder {
        return this.getMenuBuilder("Compliance", "done_all")
            .withChildMenuItem("Workplace Validation", "download.svg", "app." + reportsCommon.ReportsStateStrings.WorkplaceValidation)
            .withChildMenuItem("Workplace Requirements", "download.svg", "app." + reportsCommon.ReportsStateStrings.WorkplaceRequirements);
    }

    private createSearchAndSummationMenuItem(): MenuBuilder {
        return this.getMenuBuilder("Search & Summation", "search")
            .withChildMenuItem("Competence Search", "search", "app.compliance.competencesearch")
            .withChildMenuItem("Activity Search", "search", "app.activities.search")
            .withChildMenuItem("Activity Summation", "add_box", "app.compliance.activitysummation");
    }

    private createSetupMenuItem(): MenuBuilder {
        return this.getMenuBuilder("Setup", "list")
            .withChildMenuItem("Workplaces", "settings", "app.settings.workplaces")
            .withChildMenuItem("Work Groups", "settings", "app.resources.workgroups")
            .withChildMenuItem("Job Roles", "settings", "app.settings.jobroletypes")
            .withChildMenuItem("Job Titles", "settings", "app.settings.jobtitles")
            .withChildMenuItem("Competence Groups", "settings", "app.settings.competencegroups")
            .withChildMenuItem("Competences", "settings", "app.settings.competencetypes")
            .withChildMenuItem("CV Competence Groups", "settings", "app.settings.cvcompetencegroups")
            .withChildMenuItem("CV Competences", "settings", "app.settings.cvcompetencetypes")
            .withChildMenuItem("Activities", "settings", "app.settings.activitytypes")
            .withChildMenuItem("Activity Summation Groups", "settings", "app.settings.summationgroups")
            .withChildMenuItem("Work Schedules", "settings", "app.activities.workschedules")
            .withChildMenuItem("Authorities", "settings", "app.settings.authorities")
            .withChildMenuItem("Requirement Sections", "settings", "app.settings.requirementsections")
            .withChildMenuItem("Requirements", "settings", "app.settings.requirements");
    }

    private getMenuBuilder(title: string, icon: string, uiSref?: string, uiSrefAlias?: string): MenuBuilder {
        return new MenuBuilder(title, icon, uiSref, uiSrefAlias);
    }

    private getMenuItemBuilder(): MenuBuilder {
        return new MenuBuilder();
    }
}

class MenuBuilder {

    private childMenuBuilders: MenuBuilder[] = [];
    private parent: menuItem.MenuItem;
    private _shouldShow = true;

    constructor(private title?: string, private icon?: string, private uiSref?: string, private uiSrefAlias?: string) { }

    withChildMenuItem(title: string, icon: string, uiSref: string, uiSrefAlias?: string): MenuBuilder {
        return this.withChildMenuItemBuilder(new MenuBuilder(title, icon, uiSref, uiSrefAlias));
    }

    withChildMenuItemBuilder(childMenuBuilder: MenuBuilder): MenuBuilder {
        this.childMenuBuilders.push(childMenuBuilder);
        return this;
    }

    withParent(parent: menuItem.MenuItem): MenuBuilder {
        this.parent = parent;
        return this;
    }

    showIf(condition: boolean): MenuBuilder {
        this._shouldShow = this._shouldShow && condition;
        return this;
    }

    get shouldShow(): boolean {
        return this._shouldShow;
    }

    buildItems(rootMenuItem?: menuItem.MenuItem): menuItem.MenuItem[] {
        return this.childMenuBuilders
            .filter(childMenuBuilder => childMenuBuilder.shouldShow)
            .map(childMenuBuilder => childMenuBuilder.withParent(rootMenuItem).buildMenu())
    }

    buildMenu(): menuItem.MenuItem {
        const rootMenuItem = new menuItem.MenuItem(this.title, this.icon, this.uiSref, this.uiSrefAlias);
        rootMenuItem.childMenuItems = this.buildItems(rootMenuItem);
        rootMenuItem.parent = this.parent;
        return rootMenuItem;
    }
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/@angularclass/hmr-loader!./node_modules/tslint-loader!./client/app/core/menu.dataservice.ts